import React, { useEffect, useState } from "react";
import { Button, InputAdornment, MenuItem, TextField } from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import {
  DatePicker,
  MuiPickersUtilsProvider,
  TimePicker,
} from "@material-ui/pickers";
import Grid from "@material-ui/core/Grid";
import { Field, Form, Formik } from "formik";
import { flightsFormStyles } from "../flights.styles";
import { toggleQuickPanel } from "app/main/shared-components/quick-panel/store";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import LoadingButton from "app/main/shared-components/loading-button/LoadingButton";
import { useHistory } from "react-router-dom";
import { AppState } from "app/store";
import { CreateFlightRequestTypes } from "app/types/service-types/FlightTypes";
import { AirlineCompany } from "app/types/service-types/AirlineCompaniesTypes";
import { createFlight } from "../store/flights-list/create-flight.slice";
import { fetchAllAirlines } from "app/store/App/airlines-list.slice";
import { fetchAllAirports } from "app/store/App/airports-list.slice";
import DateUtils from "app/utils/DateUtils";
import { Autocomplete } from "@material-ui/lab";
import FlightSectorsTable from "../flight-prices-table/FlightSectorsTable";
import {
  resetTheFlightForm,
  setTheFlightForm,
} from "../store/flights-list/flight-form.slice";
import { errorMsg, warningMsg } from "../../../utils/MessageUtils";
import { fetchAllCurrencies } from "../store/currencies-list.slice";
import { useTranslation } from "react-i18next";
import { AircraftDefinitionResponse } from "app/types/service-types/AircraftTypes";
import { AircraftService } from "app/services";

const validationSchema = yup.object({
  maxNumberOfTravelers: yup
    .number()
    .typeError("Duhet të jetë numër")
    .min(1, "Numri i ulëseve duhet të jetë 1 apo më shumë"),

  totalAircraftSeats: yup
    .number()
    .typeError("Duhet të jetë numër")
    .min(1, "Totali i vendeve ne aeropolan duhet të jetë 1 apo më shumë"),

  maxOnline: yup
    .number()
    .typeError("Duhet të jetë numër")
    .min(1, "Numri i MaxOnline duhet të jetë 1 apo më shumë"),
});

const CreateFlightForm = () => {
  const classes = flightsFormStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const createFlightsState = useSelector(
    (state: AppState) => state.flights.flightsList.createFlight,
  );

  const { airlineCompanies } = useSelector(
    (state: AppState) => state.appReducers.airlines,
  );

  const airports = useSelector(
    (state: AppState) =>
      state.configurations.airport.airportsList.airports.content,
  );

  const flightForm = useSelector(
    (state: AppState) => state.flights.flightsList.flightForm,
  );

  const { currencies } = useSelector(
    (state: AppState) => state.flights.currenciesList,
  );

  const isQuickPanelOpen = useSelector((state: any) => state.quickPanel);

  const [sectorsInvalidNumberOfSeatsIds, setSectorsInvalidNumberOfSeatsIds] =
    useState<string[]>([]);

  const [isSeatCountValid, setIsSeatCountValid] = useState<boolean>(false);

  const [aircrafts, setAircrafts] = useState<AircraftDefinitionResponse[]>([]);

  const [maxOnline, setMaxOnline] = useState(
    flightForm.maxNumberOfTravelers,
  );

  useEffect(() => {
    dispatch(fetchAllAirlines());
    dispatch(fetchAllAirports());
    dispatch(fetchAllCurrencies());

    AircraftService.getAll("AVAILABLE")
      .then((data) => {
        const aircraftList = "content" in data ? data.content : data;
        setAircrafts(aircraftList);
      })
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (flightForm.publicAvailability === undefined) {
      dispatch(
        setTheFlightForm({
          ...flightForm,
          publicAvailability: true,
        }),
      );
    }
  }, [dispatch, flightForm]);

  useEffect(() => {
    if (currencies.length > 0 && !flightForm.currencyId) {
      const defaultCurrency: any = currencies.find(
        (item: any) => item?.isoCode === "EUR",
      );

      dispatch(
        setTheFlightForm({
          ...flightForm,
          maxOnline,
          currencyId: defaultCurrency?.id,
        }),
      );
    }
  }, [currencies, dispatch, flightForm, maxOnline]);

  useEffect(() => {
    let unblock: any;

    if (isQuickPanelOpen) {
      unblock = history.block(() => {
        return false;
      });
    }

    return () => {
      if (unblock) {
        unblock();
      }
    };
  }, [isQuickPanelOpen, history]);

  const availability = [
    { value: true, viewValue: "PUBLIC" },
    { value: false, viewValue: "PRIVATE" },
  ];

  const handleChangeMaxOnline = (e: any) => {
    const value = e.target.value;
    setMaxOnline(value);
  };

  const handleChange = (e: any) => {
    dispatch(
      setTheFlightForm({
        ...flightForm,
        maxOnline,
        [e.target.name]: e.target.value,
      }),
    );
  };

  const onSubmitFormRequest = (data: CreateFlightRequestTypes) => {
    const invalidSectors = flightForm.sectorPrices.filter(
      (item: any) => item.numberOfSeats < 0,
    );

    if (invalidSectors.length) {
      const invalidSectorsIds = invalidSectors.map((item: any) => item.id);
      setSectorsInvalidNumberOfSeatsIds(invalidSectorsIds);
      dispatch(warningMsg("Keni gabime në mbushjen e të dhënave"));
      return;
    }

    const departureDate = flightForm.dateOfDeparture;
    const departureTime = flightForm.timeOfDeparture;
    const arrivalTime = flightForm.timeOfArrival;

    const departureDateTime = new Date(
      `${departureDate.split("/").reverse().join("-")}T${departureTime}:00`,
    );

    let arrivalDateTime = new Date(
      `${departureDate.split("/").reverse().join("-")}T${arrivalTime}:00`,
    );

    if (arrivalDateTime.getTime() === departureDateTime.getTime()) {
      dispatch(errorMsg("Departure time cannot be equal as arrival time!"));
      return;
    }

    if (arrivalDateTime.getTime() < departureDateTime.getTime()) {
      arrivalDateTime.setDate(arrivalDateTime.getDate() + 1);
    }

    if (arrivalDateTime.getTime() <= departureDateTime.getTime()) {
      dispatch(errorMsg("Departure time cannot be greater than arrival time!"));
      return;
    }

    setSectorsInvalidNumberOfSeatsIds([]);

    dispatch(
      createFlight({
        body: {
          ...flightForm,
          maxOnline,
        },
        onSuccess: () => dispatch(resetTheFlightForm()),
      }),
    );
  };

  return (
    <div className={classes.formWrapper}>
      <div className={classes.formHeader}>
        <h1 style={{ color: "#0D3B66" }}>
          {t("app.main.flights.create.title", "Register flight")}:
        </h1>
      </div>

      <Formik
        initialValues={flightForm}
        validationSchema={validationSchema}
        onSubmit={onSubmitFormRequest}
      >
        {({ setValues }) => (
          <Form>
            <div className={classes.row}>
              <Autocomplete
                size="small"
                options={airports || []}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                value={
                  airports?.find(
                    (a) => a.id === flightForm.departureAirportId,
                  ) || null
                }
                onChange={(e, value) => {
                  dispatch(
                    setTheFlightForm({
                      ...flightForm,
                      departureAirportId: value ? value.id : "",
                      arrivalAirportId:
                        value?.id === flightForm.arrivalAirportId
                          ? ""
                          : flightForm.arrivalAirportId,
                    }),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      "app.main.flights.create.form.field1",
                      "Flight from the airport",
                    )}
                    variant="outlined"
                    required
                  />
                )}
              />

              <Autocomplete
                size="small"
                options={(airports || []).filter(
                  (a) => a.id !== flightForm.departureAirportId,
                )}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                value={
                  airports?.find((a) => a.id === flightForm.arrivalAirportId) ||
                  null
                }
                onChange={(e, value) =>
                  handleChange({
                    target: {
                      name: "arrivalAirportId",
                      value: value ? value.id : "",
                    },
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      "app.main.schedules.addPassenger.towardsTheAirport",
                      "Drejt aeroportit",
                    )}
                    variant="outlined"
                    required
                  />
                )}
              />
            </div>

            <div className={classes.row}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid
                  container
                  direction="row"
                  justify="flex-start"
                  alignItems="baseline"
                >
                  <DatePicker
                    inputVariant="outlined"
                    disableToolbar
                    autoOk
                    required
                    variant="inline"
                    className={classes.textField}
                    format="dd/MM/yyyy"
                    id="date-picker-inline"
                    label={t(
                      "app.main.flights.create.form.field2",
                      "Flight time",
                    )}
                    value={DateUtils.toISODate(
                      flightForm.dateOfDeparture.toString(),
                    )}
                    disablePast
                    onChange={(date) => {
                      if (date) {
                        handleChange({
                          target: {
                            name: "dateOfDeparture",
                            value: DateUtils.convertDate(date.toISOString()),
                          },
                        });
                      }
                    }}
                  />

                  <TimePicker
                    inputVariant="outlined"
                    format="HH:mm"
                    orientation="landscape"
                    views={["hours", "minutes"]}
                    ampm={false}
                    margin="normal"
                    label={t(
                      "app.main.flights.create.form.field3",
                      "Departure time",
                    )}
                    required
                    name="timeOfDeparture"
                    value={DateUtils.toDateObject(
                      flightForm.dateOfDeparture,
                      flightForm.timeOfDeparture,
                    )}
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300,
                    }}
                    onChange={(e) => {
                      const date = new Date();
                      const nowHour = date.getHours();
                      const nowMin = date.getMinutes();

                      const pickedHour = ("0" + e?.getHours()).substr(-2);
                      const pickedMin = ("0" + e?.getMinutes()).substr(-2);

                      const isSameDay = DateUtils.isEqualDate(
                        new Date(`${flightForm.dateOfDeparture}`),
                      );

                      if (isSameDay) {
                        if (Number(pickedHour) < Number(nowHour)) {
                          handleChange({
                            target: {
                              name: "timeOfDeparture",
                              value: `${nowHour}:${nowMin}`,
                            },
                          });
                        } else if (Number(pickedHour) === Number(nowHour)) {
                          if (Number(pickedMin) < Number(nowMin)) {
                            handleChange({
                              target: {
                                name: "timeOfDeparture",
                                value: `${nowHour}:${nowMin}`,
                              },
                            });
                          } else {
                            handleChange({
                              target: {
                                name: "timeOfDeparture",
                                value: `${pickedHour}:${pickedMin}`,
                              },
                            });
                          }
                        } else {
                          handleChange({
                            target: {
                              name: "timeOfDeparture",
                              value: `${pickedHour}:${pickedMin}`,
                            },
                          });
                        }
                      } else {
                        handleChange({
                          target: {
                            name: "timeOfDeparture",
                            value: `${pickedHour}:${pickedMin}`,
                          },
                        });
                      }
                    }}
                  />

                  <TimePicker
                    disableToolbar
                    ampm={false}
                    format="HH:mm"
                    orientation="landscape"
                    inputVariant="outlined"
                    label={t(
                      "app.main.flights.create.form.field4",
                      "Arrival time",
                    )}
                    autoComplete="true"
                    name="timeOfArrival"
                    required
                    value={DateUtils.toDateObject(
                      flightForm.dateOfDeparture,
                      flightForm.timeOfArrival,
                    )}
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={(e) => {
                      const pickedHour = ("0" + e?.getHours()).substr(-2);
                      const pickedMin = ("0" + e?.getMinutes()).substr(-2);

                      handleChange({
                        target: {
                          name: "timeOfArrival",
                          value: `${pickedHour}:${pickedMin}`,
                        },
                      });
                    }}
                  />

                  <Field
                    variant="outlined"
                    onChange={handleChange}
                    value={
                      flightForm.publicAvailability !== undefined
                        ? flightForm.publicAvailability
                        : true
                    }
                    name="publicAvailability"
                    label={t(
                      "app.main.flights.create.form.field6",
                      "Availability",
                    )}
                    className={classes.textField}
                    style={{ marginRight: 0 }}
                    select
                    as={TextField}
                  >
                    {availability.map((a: any) => (
                      <MenuItem key={String(a.value)} value={a.value}>
                        {a.viewValue}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
              </MuiPickersUtilsProvider>
            </div>

            <div className={classes.row}>
              <Field
                variant="outlined"
                onChange={handleChange}
                value={flightForm.recurringFrequencyInDays}
                name="recurringFrequencyInDays"
                label={t(
                  "app.main.flights.create.form.field7",
                  "Repeat the flight every",
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {t("app.main.flights.create.form.field8", "Day")}
                    </InputAdornment>
                  ),
                }}
                as={TextField}
              />

              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  inputVariant="outlined"
                  minDate={DateUtils.checkMinDateAtFlight(
                    flightForm.dateOfDeparture,
                    flightForm.timeOfDeparture,
                  )}
                  disableToolbar
                  autoOk
                  variant="inline"
                  format="dd/MM/yyyy"
                  id="date-picker-inline"
                  label={t(
                    "app.main.flights.create.form.field9",
                    "Until the date",
                  )}
                  value={DateUtils.toISODate(
                    flightForm.recurringEndDate.toString(),
                  )}
                  disablePast
                  onChange={(date) => {
                    if (date) {
                      handleChange({
                        target: {
                          name: "recurringEndDate",
                          value: DateUtils.convertDate(date.toISOString()),
                        },
                      });
                    }
                  }}
                />
              </MuiPickersUtilsProvider>
            </div>

            <div className={classes.row}>
              <Field
                name="totalAircraftSeats"
                render={({ field, form }: any) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t(
                      "app.main.flights.create.form.field17",
                      "Aircraft seats",
                    )}
                    variant="outlined"
                    required
                    error={Boolean(
                      form.errors.totalAircraftSeats &&
                        form.touched.totalAircraftSeats,
                    )}
                    value={flightForm.totalAircraftSeats}
                    onChange={(e: any) => {
                      const totalAircraftSeatsValue = e.target.value;

                      setValues((values) => ({
                        ...values,
                        totalAircraftSeats: totalAircraftSeatsValue,
                      }));

                      handleChange(e);
                    }}
                    helperText={form.errors.totalAircraftSeats}
                  />
                )}
              />

              <Field
                name="maxNumberOfTravelers"
                render={({ form }: any) => (
                  <TextField
                    type="text"
                    label={t(
                      "app.main.flights.create.form.field0",
                      "Max number of travelers",
                    )}
                    variant="outlined"
                    name="maxNumberOfTravelers"
                    value={flightForm.maxNumberOfTravelers}
                    error={Boolean(form.errors.maxNumberOfTravelers)}
                    required
                    onChange={(e: any) => {
                      const maxNumberOfTravelers = e.target.value;

                      setValues((values) => ({
                        ...values,
                        maxNumberOfTravelers,
                        maxOnline: maxNumberOfTravelers,
                      }));

                      setMaxOnline(maxNumberOfTravelers);
                      handleChange(e);
                    }}
                    helperText={form.errors.maxNumberOfTravelers}
                  />
                )}
              />

              <Field
                variant="outlined"
                onChange={handleChange}
                value={flightForm.gepack}
                name="gepack"
                label="Gepack"
                as={TextField}
              />

              <Field
                variant="outlined"
                onChange={handleChange}
                value={flightForm.gepackINF}
                name="gepackINF"
                label="Gepack INF"
                as={TextField}
              />
            </div>

            <div className={classes.row}>
              <Field
                name="maxOnline"
                render={({ form }: any) => (
                  <TextField
                    type="text"
                    variant="outlined"
                    value={maxOnline}
                    error={Boolean(form.errors.maxOnline)}
                    onChange={handleChangeMaxOnline}
                    helperText={form.errors.maxOnline}
                    name="maxOnline"
                    label={t(
                      "app.main.flights.create.form.field11",
                      "Booking Engine Allotment",
                    )}
                  />
                )}
              />

              <Field
                variant="outlined"
                onChange={handleChange}
                value={flightForm.increasePriceOfOnlineBookingFor}
                name="increasePriceOfOnlineBookingFor"
                label={t(
                  "app.main.flights.create.form.field12",
                  "Increase Price Of Online Booking For",
                )}
                as={TextField}
              />
            </div>

            <div className={classes.row}>
              <Field
                variant="outlined"
                onChange={handleChange}
                value={flightForm.airlineCompanyId}
                name="airlineCompanyId"
                label={t(
                  "app.main.flights.create.form.field13",
                  "Flight operator",
                )}
                select
                as={TextField}
                required
              >
                {airlineCompanies &&
                  airlineCompanies.map((a: AirlineCompany, index: number) => (
                    <MenuItem key={a.id + index} id={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
              </Field>

              <Field
                variant="outlined"
                onChange={(e: any) => {
                  const selectedAircraft = aircrafts.find(
                    (aircraft) => aircraft.id === e.target.value,
                  );

                  dispatch(
                    setTheFlightForm({
                      ...flightForm,
                      aircraftId: e.target.value,
                      totalAircraftSeats: selectedAircraft
                        ? selectedAircraft.totalSeats
                        : flightForm.totalAircraftSeats,
                    }),
                  );
                }}
                value={flightForm.aircraftId || ""}
                name="aircraftId"
                label={t("app.main.flights.create.form.aircraft", "Aircraft")}
                select
                as={TextField}
              >
                <MenuItem value="">
                  <em>{t("app.main.flights.create.form.noAircraft", "None")}</em>
                </MenuItem>

                {aircrafts.map((aircraft: AircraftDefinitionResponse) => (
                  <MenuItem key={aircraft.id} value={aircraft.id}>
                    {aircraft.name} ({aircraft.code}) — {aircraft.totalSeats} seats
                  </MenuItem>
                ))}
              </Field>
            </div>

            <div className={classes.row}>
              <Field
                type="number"
                variant="outlined"
                onChange={handleChange}
                value={flightForm.taxOfFlight}
                name="taxOfFlight"
                label={t(
                  "app.main.flights.create.form.field14",
                  "Taxes: (EUR)",
                )}
                as={TextField}
                InputProps={{ inputProps: { min: 0 } }}
                required
              />

              <Field
                variant="outlined"
                onChange={handleChange}
                value={flightForm.number.toUpperCase()}
                name="number"
                label={t(
                  "app.main.flights.create.form.field15",
                  "Flight number",
                )}
                as={TextField}
                required
              />
            </div>

            <div className={classes.row}>
              <Field
                onChange={handleChange}
                variant="outlined"
                value={flightForm.currencyId}
                name="currencyId"
                required
                label={t("app.main.flights.create.form.field16", "Currency")}
                select
                style={{ minWidth: 120 }}
                as={TextField}
              >
                {currencies &&
                  currencies
                    .filter((currency) =>
                      ["EUR", "CHF", "USD"].includes(currency.isoCode),
                    )
                    .map((currency: any, index: number) => (
                      <MenuItem key={index} value={currency.id}>
                        {currency.name} - {currency.isoCode}
                      </MenuItem>
                    ))}
              </Field>
            </div>

            <div className={classes.row}>
              <FlightSectorsTable
                sectorsInvalidNumberOfSeatsIds={sectorsInvalidNumberOfSeatsIds}
                onSeatCountValidityChange={setIsSeatCountValid}
              />
            </div>

            <div className={classes.row}>
              <LoadingButton
                type="submit"
                btnTitle={t(
                  "app.main.schedules.update.form.submitButton",
                  "Submit",
                )}
                isLoading={createFlightsState.loading}
                disabled={!isSeatCountValid}
              />

              <Button
                onClick={() => {
                  dispatch(toggleQuickPanel());
                  history.push("/flights/flights-list");
                  dispatch(resetTheFlightForm());
                }}
              >
                {t("app.main.schedules.splitPassenger.closeButton", "Close")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateFlightForm;